import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL } from "@/theme/tokens";

export type ChatMessageSender = "user" | "assistant";

type Props = {
	/** Which side the bubble sits on: user = trailing/brand, assistant = leading/neutral. */
	sender: ChatMessageSender;
	children: ReactNode;
	/** "error" renders an assistant-side bubble in the status error palette. */
	tone?: "default" | "error";
	/** Cap on bubble width inside the message column. Default "85%". */
	maxWidth?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

// Mount fade + tiny lift (transform/opacity only); reduced-motion drops it.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes chatBubbleEnter": {
		from: { opacity: 0, transform: "translateY(4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `chatBubbleEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * One chat message bubble for a flex-column message list. Owns its side
 * alignment (via alignSelf), palette, and asymmetric "tail" radius; the parent
 * owns the column layout and any avatar/timestamp chrome.
 */
export const ChatMessageBubble: FC<Props> = ({ sender, children, tone = "default", maxWidth = "85%", dataTestId, sx }) => {
	const isUser = sender === "user";
	return (
		<Box
			data-testid={dataTestId}
			sx={[
				ENTRANCE_SX,
				(theme) => ({
					alignSelf: isUser ? "flex-end" : "flex-start",
					width: "fit-content",
					maxWidth,
					px: 1.75,
					py: 1.25,
					borderRadius: 2,
					// The "tail" corner flattens toward the sender's side.
					...(isUser ? { borderBottomRightRadius: 4 } : { borderBottomLeftRadius: 4 }),
					fontSize: FONT_SIZE.EMPHASIS,
					lineHeight: 1.5,
					overflowWrap: "break-word",
					...(tone === "error"
						? { backgroundColor: theme.palette.error.light, color: theme.palette.error.dark }
						: isUser
							? { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }
							: { backgroundColor: NEUTRAL[150], color: INK.PRIMARY }),
				}),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{children}
		</Box>
	);
};
